import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import { ProductVariation } from 'models/productsReferential';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { getUserPreferencesPinnedProductsVariations } from 'state/selectors';
import { ThunkDispatch } from 'state/state.common';
import { userPreferencesPinnedProductsVariationsChangedThunk } from 'state/userPreferences/userPreferences.thunks';
import { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { optionalMap } from 'utils/optional';
import { getProductNameForAnalytics, isEqual } from 'utils/productVariation';

export function usePinnedProducts(workspace: WorkspaceWithProductSelector) {
  const dispatch = useDispatch<ThunkDispatch>();
  const notification = useUserNotification();
  const toLabel = optionalMap(useToProductTypeLabel());
  const { formatMessage } = useIntl();

  const pinnedProducts = useSelector(getUserPreferencesPinnedProductsVariations(workspace));

  const isProductPinned = useCallback(
    (productVariation?: ProductVariation) => {
      if (!productVariation) {
        return false;
      }

      return pinnedProducts.some(pinnedProduct => isEqual(pinnedProduct, productVariation));
    },
    [pinnedProducts],
  );

  const handlePinToggle = useCallback(
    ({ productVariation }: { productVariation?: ProductVariation; tabType: ProductSelectorTabType }) => {
      if (!productVariation) {
        return;
      }

      const isPinned = isProductPinned(productVariation);
      const values = {
        productType:
          toLabel(productVariation.commercialProductType) ??
          formatMessage({
            id: `quote.products.${productVariation.productType}` as IntlKey,
            defaultMessage: productVariation.productType,
          }),
        productSubtype:
          toLabel(productVariation.commercialProductSubType) ??
          formatMessage({
            id: `quote.products.${productVariation.productSubType}` as IntlKey,
            defaultMessage: productVariation.productSubType,
          }),
        wrapper: formatMessage({ id: `quote.products.${productVariation.wrapper}` as IntlKey }),
      };

      dispatch(
        userPreferencesPinnedProductsVariationsChangedThunk({
          workspace,
          action: isPinned ? 'unpin' : 'pin',
          productVariation: productVariation,
        }),
      );

      logAnalytics.trackEvent(
        'Primary market essential product selector',
        isPinned ? 'Unpin product' : 'Pin product',
        getProductNameForAnalytics(productVariation),
      );

      notification.success({
        title: { id: isPinned ? 'toast.productUnpinned.title' : 'toast.productPinned.title' },
        content: [
          {
            id: isPinned ? 'toast.productUnpinned.content' : 'toast.productPinned.content',
            values,
          },
        ],
      });
    },
    [toLabel, formatMessage, notification, dispatch, isProductPinned, workspace],
  );

  return { isProductPinned, handlePinToggle };
}
